import { CopyField, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@westhetics/ui'
import type { ReactNode } from 'react'
import { DOCS_MCP_URL, MCP_URL } from './labels'

/**
 * "Conectar um assistente" (docs/areas/integracoes.md RN-INT-21; docs/api/mcp.md): o endereço do MCP e o passo a
 * passo de cada cliente, numa aba por cliente. Serve a toda pessoa (a aba Aplicativos conectados da conta) e à
 * gestora (Integrações), que vê também a conexão pela chave de um app, para automação e desenvolvimento.
 */

interface Client {
  value: string
  label: string
  steps: ReactNode[]
  command?: { label: string; value: string }
}

const CLIENTS: Client[] = [
  {
    value: 'claude',
    label: 'Claude',
    steps: [
      'No Claude (no navegador ou no aplicativo), abra Personalizar › Conectores (Customize › Connectors).',
      'Em + Adicionar › Adicionar conector personalizado (Add custom connector), dê o nome Westhetics, cole o endereço acima e, no cliente OAuth, escolha Registrar automaticamente (Register automatically).',
      'Clique em Conectar (Connect), entre com a sua conta da Westhetics e autorize.',
    ],
  },
  {
    value: 'chatgpt',
    label: 'ChatGPT',
    steps: [
      'No ChatGPT, abra Plugins (em algumas contas, Configurações › Apps e conectores, com o modo de desenvolvedor ligado nas configurações avançadas).',
      'Em + › Adicionar servidor MCP personalizado (Add custom MCP server), dê o nome Westhetics, cole o endereço acima, escolha a autenticação OAuth e confirme o aviso.',
      'Entre com a sua conta da Westhetics e autorize. Na conversa, digite @ e escolha Westhetics.',
    ],
  },
  {
    value: 'claude-code',
    label: 'Claude Code',
    command: {
      label: 'No terminal',
      value: `claude mcp add --transport http westhetics ${MCP_URL}`,
    },
    steps: [
      'Rode o comando acima no terminal.',
      'No Claude Code, digite /mcp, escolha westhetics e entre com a sua conta da Westhetics.',
    ],
  },
  {
    value: 'codex',
    label: 'Codex',
    command: {
      label: 'No terminal',
      value: `codex mcp add westhetics --url ${MCP_URL}\ncodex mcp login westhetics`,
    },
    steps: [
      'Rode os dois comandos acima no terminal.',
      'O navegador abre: entre com a sua conta da Westhetics e autorize.',
    ],
  },
  {
    value: 'cursor',
    label: 'Cursor',
    command: {
      label: 'Em ~/.cursor/mcp.json',
      value: `{\n  "mcpServers": {\n    "westhetics": { "url": "${MCP_URL}" }\n  }\n}`,
    },
    steps: [
      'Em Cursor Settings, abra MCP e adicione um servidor, ou cole o trecho acima no mcp.json.',
      'O Cursor pede para entrar: use a sua conta da Westhetics e autorize.',
    ],
  },
]

const KEY_CLIENT: Client = {
  value: 'key',
  label: 'Com a chave do app',
  command: {
    label: 'No terminal',
    value: `claude mcp add --transport http westhetics ${MCP_URL} \\\n  --header "Authorization: Bearer wx_SUA_CHAVE"`,
  },
  steps: [
    'Para automação e desenvolvimento: o assistente age como o app, só com as permissões dele, sem entrar com uma pessoa.',
    'Troque wx_SUA_CHAVE pela chave do app. A chave não vence; revogue o app quando parar de usar.',
  ],
}

const EXAMPLES = [
  'O que está pendente hoje na clínica?',
  'Cadastre a Amanda Ribeiro, WhatsApp 11 98765-4321, e mande a anamnese pelo link.',
  'Quais contratos foram assinados esta semana? E quais ainda esperam a paciente?',
]

const stepClass = 'font-body text-body text-ink leading-[1.7] font-light'
const noteClass = 'font-body text-secondary text-[12px] leading-[1.6] font-light'

export function AssistantGuide({ withKey = false }: { withKey?: boolean }) {
  const clients = withKey ? [...CLIENTS, KEY_CLIENT] : CLIENTS
  return (
    <div className="flex flex-col gap-5">
      <p className="font-body text-body text-secondary max-w-[640px] leading-[1.7] font-light">
        O assistente entra com a sua conta e faz o que o seu perfil faz: busca e cadastra pacientes,
        manda anamnese, gera e envia o contrato e diz o que está pendente hoje. Antes de mandar
        mensagem a uma paciente, ele pede a sua confirmação.
      </p>

      <CopyField
        label="Endereço do MCP"
        value={MCP_URL}
        copyAriaLabel="Copiar o endereço do MCP"
        onCopied={() => toast('Endereço copiado')}
      />

      <Tabs defaultValue="claude">
        <TabsList aria-label="Onde conectar">
          {clients.map((client) => (
            <TabsTrigger key={client.value} value={client.value}>
              {client.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {clients.map((client) => (
          <TabsContent key={client.value} value={client.value} className="flex flex-col gap-4 pt-5">
            {client.command ? (
              <CopyField
                multiline
                label={client.command.label}
                value={client.command.value}
                copyLabel="Copiar comando"
                copyAriaLabel={`Copiar o comando do ${client.label}`}
                onCopied={() => toast('Comando copiado')}
              />
            ) : null}
            <ol className="flex flex-col gap-2">
              {client.steps.map((step, index) => (
                <li key={index} className="flex gap-3">
                  <span className="font-display text-ink w-4 shrink-0 text-[13px] leading-[1.7] font-normal">
                    {index + 1}
                  </span>
                  <span className={stepClass}>{step}</span>
                </li>
              ))}
            </ol>
          </TabsContent>
        ))}
      </Tabs>

      <div className="border-hairline border-t pt-4">
        <div className="eyebrow">Peça assim</div>
        <ul className="mt-3 flex flex-col gap-2">
          {EXAMPLES.map((example) => (
            <li key={example} className={`${stepClass} border-lilac border-l-2 pl-3`}>
              {example}
            </li>
          ))}
        </ul>
      </div>

      <p className={noteClass}>
        A conexão vale 12 horas; depois, o assistente pede para entrar de novo. O que ele faz fica
        na trilha com o seu nome. Guia completo, com o que cada ferramenta faz, em{' '}
        <a
          href={DOCS_MCP_URL}
          target="_blank"
          rel="noreferrer"
          className="text-ink border-hairline-heavy hover:border-ink border-b"
        >
          docs.westhetics.io/mcp
        </a>
        .
      </p>
    </div>
  )
}
